import * as THREE from 'three';

import { useEffect, useRef, useState } from 'react';

import { Billboard } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';

// Simple particle system for hit effects (copied from SimpleNaruto for consistency)
const HitParticles = ({ visible }: { visible: boolean }) => {
  const particlesRef = useRef<THREE.Group>(null);
  const particles = useRef<THREE.Mesh[]>([]);
  const velocities = useRef<THREE.Vector3[]>([]);

  // Create particles on mount
  useEffect(() => {
    if (!particlesRef.current) return;

    // Create 10 particles
    for (let i = 0; i < 10; i++) {
      const particle = new THREE.Mesh(
        new THREE.SphereGeometry(0.05, 8, 8),
        new THREE.MeshBasicMaterial({ color: '#FF0000' })
      );

      // Position particles at center first
      particle.position.set(0, 0, 0);
      particle.visible = false;

      particlesRef.current.add(particle);
      particles.current.push(particle);

      // Create random velocity
      velocities.current.push(
        new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          Math.random() * 2,
          (Math.random() - 0.5) * 2
        )
      );
    }
  }, []);

  // Reset and show particles when becoming visible
  useEffect(() => {
    if (!visible || !particlesRef.current) return;

    // Reset all particles to center and make visible
    particles.current.forEach((particle, i) => {
      particle.position.set(0, 0.5, 0); // Start slightly above center
      particle.visible = true;

      // Refresh velocities
      velocities.current[i] = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        Math.random() * 2,
        (Math.random() - 0.5) * 2
      );
    });

    // Hide particles after animation completes
    const timeout = setTimeout(() => {
      particles.current.forEach((particle) => {
        particle.visible = false;
      });
    }, 500);

    return () => clearTimeout(timeout);
  }, [visible]);

  // Animate particles
  useFrame((_, delta) => {
    if (!visible || !particlesRef.current) return;

    particles.current.forEach((particle, i) => {
      if (!particle.visible) return;

      // Move particle
      particle.position.add(
        velocities.current[i].clone().multiplyScalar(delta * 5)
      );

      // Apply gravity
      velocities.current[i].y -= delta * 5;

      // Fade out by scaling down
      particle.scale.multiplyScalar(0.95);
    });
  });

  return <group ref={particlesRef} />;
};

interface SimpleGeninProps {
  npcId: string;
  position: [number, number, number];
  facingDirection: [number, number, number];
  health: number;
  behaviorState: 'patrolling' | 'fleeing' | 'dead';
}

export function SimpleGenin({
  position,
  facingDirection = [0, 0, 1],
  health = 100,
  behaviorState = 'patrolling',
}: SimpleGeninProps) {
  const groupRef = useRef<THREE.Group>(null);
  const [hitFlash, setHitFlash] = useState(false);

  // Add animation states for low-poly model
  const [armRotation, setArmRotation] = useState(0);
  const [legRotation, setLegRotation] = useState(0);
  const animationSpeedRef = useRef(0);
  const isMovingRef = useRef(behaviorState !== 'dead');

  // Set position and rotation based on props
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.position.set(position[0], position[1], position[2]);

      // Only update rotation if we have a valid facing direction
      if (
        facingDirection &&
        (facingDirection[0] !== 0 || facingDirection[2] !== 0)
      ) {
        // Calculate the angle from the facing direction
        const angle = Math.atan2(facingDirection[0], facingDirection[2]);
        groupRef.current.rotation.y = angle;
      }
    }

    // Update animation speed based on behavior
    if (behaviorState === 'fleeing') {
      animationSpeedRef.current = 8; // Fast animation for fleeing
      isMovingRef.current = true;
    } else if (behaviorState === 'patrolling') {
      animationSpeedRef.current = 4; // Normal speed for patrolling
      isMovingRef.current = true;
    } else {
      animationSpeedRef.current = 0; // No animation for dead
      isMovingRef.current = false;
    }
  }, [position, facingDirection, behaviorState]);

  // Flash red when hit (can be triggered when health decreases)
  useEffect(() => {
    if (hitFlash) {
      const timer = setTimeout(() => setHitFlash(false), 200);
      return () => clearTimeout(timer);
    }
  }, [hitFlash]);

  // Watch for health changes to trigger hit flash
  const prevHealthRef = useRef(health);
  useEffect(() => {
    if (health < prevHealthRef.current) {
      setHitFlash(true);
    }
    prevHealthRef.current = health;
  }, [health]);

  // Animate limbs for walking
  useFrame((state) => {
    if (isMovingRef.current && animationSpeedRef.current > 0) {
      const time = state.clock.getElapsedTime();
      setArmRotation(Math.sin(time * animationSpeedRef.current) * 0.5);
      setLegRotation(Math.sin(time * animationSpeedRef.current) * 0.5);
    } else {
      // Gradually return to neutral pose
      setArmRotation(armRotation * 0.9);
      setLegRotation(legRotation * 0.9);
    }
  });

  // Determine color based on state and hit flash
  const getColor = () => {
    if (hitFlash) return '#FF0000'; // Red when hit
    if (behaviorState === 'dead') return '#888888'; // Gray when dead
    if (behaviorState === 'fleeing') return '#FFA500'; // Orange when fleeing
    return '#2851A3'; // Blue when patrolling (standard genin color - dark blue)
  };

  // Determine headband color
  const getHeadbandColor = () => {
    if (hitFlash) return '#FF9999'; // Lighter red when hit
    return '#4C4C4C'; // Dark gray normally
  };

  return (
    <group ref={groupRef}>
      {/* Health bar using Billboard to always face camera */}
      <Billboard
        position={[0, 2.2, 0]}
        follow={true}
        lockX={false}
        lockY={false}
        lockZ={false}
      >
        <mesh position={[0, 0, 0]} scale={[1, 0.15, 0.05]}>
          <boxGeometry />
          <meshBasicMaterial color="#333333" />
        </mesh>
        <mesh
          position={[(health / 100 - 1) / 2, 0, 0.03]}
          scale={[health / 100, 0.12, 0.05]}
        >
          <boxGeometry />
          <meshBasicMaterial
            color={
              health > 50 ? '#00FF00' : health > 20 ? '#FFFF00' : '#FF0000'
            }
          />
        </mesh>
      </Billboard>

      {/* Hit particles effect */}
      <HitParticles visible={hitFlash} />

      {/* Low-poly genin model - using boxes for the blocky style */}
      {/* Body */}
      <mesh castShadow position={[0, 0.45, 0]}>
        <boxGeometry args={[0.8, 1.1, 0.4]} />
        <meshLambertMaterial color={getColor()} flatShading />
      </mesh>

      {/* Head */}
      <mesh position={[0, 1.2, 0]} castShadow>
        <boxGeometry args={[0.7, 0.7, 0.7]} />
        <meshLambertMaterial color="#E6C7A5" flatShading /> {/* Skin tone */}
      </mesh>

      {/* Forehead protector */}
      <mesh position={[0, 1.3, 0.4]} castShadow>
        <boxGeometry args={[0.75, 0.2, 0.1]} />
        <meshLambertMaterial color={getHeadbandColor()} flatShading />
      </mesh>

      {/* Metal plate on forehead protector */}
      <mesh position={[0, 1.3, 0.5]} castShadow>
        <boxGeometry args={[0.4, 0.15, 0.05]} />
        <meshLambertMaterial color="#B0BEC5" flatShading />
      </mesh>

      {/* Eyes */}
      <mesh position={[0.15, 1.2, 0.4]} castShadow>
        <boxGeometry args={[0.1, 0.1, 0.05]} />
        <meshBasicMaterial color="black" />
      </mesh>

      <mesh position={[-0.15, 1.2, 0.4]} castShadow>
        <boxGeometry args={[0.1, 0.1, 0.05]} />
        <meshBasicMaterial color="black" />
      </mesh>

      {/* Arms */}
      <group position={[0.5, 0.8, 0]} rotation={[armRotation, 0, 0]}>
        <mesh position={[0, -0.3, 0]} castShadow>
          <boxGeometry args={[0.2, 0.6, 0.2]} />
          <meshLambertMaterial color={getColor()} flatShading />
        </mesh>
      </group>

      <group position={[-0.5, 0.8, 0]} rotation={[-armRotation, 0, 0]}>
        <mesh position={[0, -0.3, 0]} castShadow>
          <boxGeometry args={[0.2, 0.6, 0.2]} />
          <meshLambertMaterial color={getColor()} flatShading />
        </mesh>
      </group>

      {/* Legs */}
      <group position={[0.25, 0, 0]} rotation={[legRotation, 0, 0]}>
        <mesh position={[0, -0.2, 0]} castShadow>
          <boxGeometry args={[0.2, 0.4, 0.2]} />
          <meshLambertMaterial color="#1A237E" flatShading /> {/* Dark blue */}
        </mesh>
        <mesh position={[0, -0.45, 0.1]} castShadow>
          <boxGeometry args={[0.25, 0.2, 0.4]} />
          <meshLambertMaterial color="#0D47A1" flatShading />{' '}
          {/* Slightly darker blue */}
        </mesh>
      </group>

      <group position={[-0.25, 0, 0]} rotation={[-legRotation, 0, 0]}>
        <mesh position={[0, -0.2, 0]} castShadow>
          <boxGeometry args={[0.2, 0.4, 0.2]} />
          <meshLambertMaterial color="#1A237E" flatShading />
        </mesh>
        <mesh position={[0, -0.45, 0.1]} castShadow>
          <boxGeometry args={[0.25, 0.2, 0.4]} />
          <meshLambertMaterial color="#0D47A1" flatShading />
        </mesh>
      </group>

      {/* Shadow */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.01, 0]}
        receiveShadow
      >
        <circleGeometry args={[0.3, 32]} />
        <meshBasicMaterial color="black" transparent opacity={0.3} />
      </mesh>
    </group>
  );
}
